import React, { useState, useEffect, useRef } from 'react';
import { PlusCircle, CheckCircle, List, EyeOff, Edit, Eye, XCircle, ArchiveRestore, Trash2 } from 'lucide-react';

const ProcessoSeletivoGestaoVagas = () => {
    const [cargos, setCargos] = useState([]);
    const [todasVagas, setTodasVagas] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isFetching, setIsFetching] = useState(true);

    // Controle de Edição
    const [modoEdicao, setModoEdicao] = useState(false);
    const [vagaEditandoId, setVagaEditandoId] = useState(null);
    const formRef = useRef(null);

    const estadoInicialForm = { cargo: '', salario: '', requisitos: '', escalas: '', turnos: '', beneficios: '' };
    const [formData, setFormData] = useState(estadoInicialForm);

    const carregarDados = async () => {
        try {
            const token = localStorage.getItem('authToken');
            const headers = { 'Authorization': `Bearer ${token}` };

            const [resCargos, resVagas] = await Promise.all([
                fetch('/api/processo-seletivo/cargos-disponiveis', { headers }),
                fetch('/api/processo-seletivo/todas-vagas', { headers }) // Agora puxa todas as vagas
            ]);

            if (resCargos.ok) setCargos(await resCargos.json());
            if (resVagas.ok) setTodasVagas(await resVagas.json());
            
            setIsFetching(false);
        } catch (error) {
            console.error("Erro ao carregar dados:", error);
            setIsFetching(false);
        }
    };

    useEffect(() => { carregarDados(); }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    // --- SALVAR (NOVA OU EDIÇÃO) ---
    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        const token = localStorage.getItem('authToken');
        const url = modoEdicao ? `/api/processo-seletivo/editar-vaga/${vagaEditandoId}` : '/api/processo-seletivo/nova-vaga';
        const method = modoEdicao ? 'PUT' : 'POST';

        try {
            const response = await fetch(url, {
                method: method,
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify(formData)
            });

            if (!response.ok) throw new Error("Erro ao salvar vaga");

            alert(modoEdicao ? "Vaga atualizada com sucesso!" : "Vaga publicada com sucesso!");
            cancelarEdicao();
            carregarDados(); 
        } catch (error) {
            alert("Ocorreu um erro ao salvar a vaga.");
        } finally {
            setIsLoading(false);
        }
    };

    // --- AÇÕES DOS BOTÕES ---
    const handleInativar = async (idVaga) => {
        if (!window.confirm("Remover esta vaga do site?")) return;
        const token = localStorage.getItem('authToken');
        await fetch('/api/processo-seletivo/inativar-vaga', {
            method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({ idVaga })
        });
        carregarDados();
    };

    const handleAtivar = async (idVaga) => {
        if (!window.confirm("Deseja republicar esta vaga no site?")) return;
        const token = localStorage.getItem('authToken');
        await fetch('/api/processo-seletivo/ativar-vaga', {
            method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({ idVaga })
        });
        carregarDados();
    };

    const handleExcluir = async (idVaga) => {
        if (!window.confirm("Atenção: Esta ação é irreversível. Deseja realmente excluir esta vaga do banco de dados?")) return;
        
        setIsLoading(true);
        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`/api/processo-seletivo/excluir-vaga/${idVaga}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!response.ok) throw new Error("Erro ao excluir vaga");
            
            carregarDados();
        } catch (error) {
            alert("Ocorreu um erro ao excluir a vaga.");
        } finally {
            setIsLoading(false);
        }
    };

    const iniciarEdicao = (vaga) => {
        setModoEdicao(true);
        setVagaEditandoId(vaga.ID);
        setFormData({
            cargo: vaga.CARGO, salario: vaga.SALARIO, requisitos: vaga.REQUISITOS,
            escalas: vaga.ESCALAS, turnos: vaga.TURNOS, beneficios: vaga.BENEFICIOS
        });
        // Rola a tela suavemente para o formulário
        if(formRef.current) formRef.current.scrollIntoView({ behavior: 'smooth' });
    };

    const cancelarEdicao = () => {
        setModoEdicao(false);
        setVagaEditandoId(null);
        setFormData(estadoInicialForm);
    };

    // Filtros visuais
    const vagasAtivas = todasVagas.filter(v => v.STATUS === 1);
    const vagasInativas = todasVagas.filter(v => v.STATUS === 0);

    const inputStyle = { width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '14px', backgroundColor: '#f8fafc', color: '#334155', boxSizing: 'border-box' };
    const labelStyle = { display: 'block', fontSize: '13px', fontWeight: '800', color: '#475569', marginBottom: '8px', textTransform: 'uppercase' };

    if (isFetching) return <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>Carregando painel de vagas...</div>;

    return (
        <div style={{ animation: 'fadeIn 0.3s ease-in-out' }}>
            <div style={{ marginBottom: '24px' }}>
                <h2 style={{ fontSize: '24px', fontWeight: '900', color: '#0f172a', marginBottom: '8px', margin: 0 }}>Gestão de Vagas</h2>
                <p style={{ color: '#64748b', fontSize: '15px', margin: 0 }}>Crie, publique, edite e encerre anúncios de vagas diretamente na página de inscrição.</p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px' }}>
                
                {/* --- FORMULÁRIO (CRIAÇÃO / EDIÇÃO) --- */}
                <div ref={formRef} style={{ backgroundColor: '#fff', borderRadius: '16px', border: `2px solid ${modoEdicao ? '#f59e0b' : '#e2e8f0'}`, padding: '24px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: '1px solid #f1f5f9', paddingBottom: '16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {modoEdicao ? <Edit color="#f59e0b" size={24} /> : <PlusCircle color="#e11d48" size={24} />}
                            <h3 style={{ fontSize: '18px', fontWeight: '800', color: '#0f172a', margin: 0 }}>
                                {modoEdicao ? 'Editar Vaga Oculta' : 'Publicar Nova Vaga'}
                            </h3>
                        </div>
                        {modoEdicao && (
                            <button onClick={cancelarEdicao} style={{ display: 'flex', alignItems: 'center', gap: '4px', background: 'transparent', border: 'none', color: '#64748b', fontWeight: '700', cursor: 'pointer' }}>
                                <XCircle size={16} /> Cancelar
                            </button>
                        )}
                    </div>

                    <form onSubmit={handleSubmit}>
                        <div style={{ marginBottom: '16px' }}>
                            <label style={labelStyle}>Cargo</label>
                            <select name="cargo" value={formData.cargo} onChange={handleChange} style={inputStyle} required>
                                <option value="">Selecione o cargo...</option>
                                {cargos.map((c, idx) => (
                                    <option key={idx} value={c.NOME_CARGO_ABREV}>{c.NOME_CARGO_ABREV}</option>
                                ))}
                            </select>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                            <div>
                                <label style={labelStyle}>Salário</label>
                                <input type="text" name="salario" value={formData.salario} onChange={handleChange} style={inputStyle} placeholder="Ex: R$ 1.705,00" required />
                            </div>
                            <div>
                                <label style={labelStyle}>Escalas</label>
                                <input type="text" name="escalas" value={formData.escalas} onChange={handleChange} style={inputStyle} placeholder="Ex: 12x36 ou 5x2" required />
                            </div>
                        </div>

                        <div style={{ marginBottom: '16px' }}>
                            <label style={labelStyle}>Turnos</label>
                            <input type="text" name="turnos" value={formData.turnos} onChange={handleChange} style={inputStyle} placeholder="Ex: Matutino ou Noturno (horários a combinar)" required />
                        </div>

                        <div style={{ marginBottom: '16px' }}>
                            <label style={labelStyle}>Requisitos & Perfil</label>
                            <textarea name="requisitos" value={formData.requisitos} onChange={handleChange} style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }} placeholder="Ex: Não precisa de experiência. Precisa ter mais de 18 anos." required />
                        </div>

                        <div style={{ marginBottom: '24px' }}>
                            <label style={labelStyle}>Benefícios <span style={{ textTransform: 'none', color: '#94a3b8', fontWeight: '500' }}>(Pressione Enter para separar)</span></label>
                            <textarea name="beneficios" value={formData.beneficios} onChange={handleChange} style={{ ...inputStyle, minHeight: '120px', resize: 'vertical' }} placeholder="Refeição no local&#10;Vale-transporte..." required />
                        </div>

                        <button 
                            type="submit" 
                            disabled={isLoading}
                            style={{ width: '100%', padding: '14px', backgroundColor: isLoading ? '#94a3b8' : (modoEdicao ? '#f59e0b' : '#e11d48'), color: '#fff', fontSize: '15px', fontWeight: '800', border: 'none', borderRadius: '8px', cursor: isLoading ? 'not-allowed' : 'pointer', transition: 'background-color 0.2s' }}
                        >
                            {isLoading ? 'Salvando...' : (modoEdicao ? 'Salvar Alterações' : 'Publicar Vaga no Site')}
                        </button>
                    </form>
                </div>

                {/* --- LISTAGEM DE VAGAS --- */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                    
                    {/* VAGAS ATIVAS */}
                    <div style={{ backgroundColor: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0', padding: '24px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px', borderBottom: '1px solid #f1f5f9', paddingBottom: '16px' }}>
                            <List color="#10b981" size={24} />
                            <h3 style={{ fontSize: '18px', fontWeight: '800', color: '#0f172a', margin: 0 }}>No Site Agora ({vagasAtivas.length})</h3>
                        </div>

                        {vagasAtivas.length === 0 ? (
                            <p style={{ color: '#64748b', fontSize: '14px', textAlign: 'center' }}>Nenhuma vaga ativa no site.</p>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                {vagasAtivas.map(vaga => (
                                    <div key={vaga.ID} style={{ padding: '16px', borderRadius: '8px', border: '1px solid #e2e8f0', backgroundColor: '#f8fafc' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                            <strong style={{ color: '#0f172a', fontSize: '15px' }}>{vaga.CARGO}</strong>
                                            {/* Substitua a div de botões das Vagas Ativas por esta: */}
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                <span style={{ backgroundColor: '#dcfce7', color: '#059669', padding: '4px 8px', borderRadius: '9999px', fontSize: '11px', fontWeight: '800' }}>Ativa</span>
                                                <button onClick={() => handleInativar(vaga.ID)} style={{ background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer' }} title="Ocultar do Site"><EyeOff size={18} /></button>
                                                <button onClick={() => handleExcluir(vaga.ID)} style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer' }} title="Excluir Definitivamente"><Trash2 size={18} /></button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* VAGAS INATIVAS / ARQUIVADAS */}
                    <div style={{ backgroundColor: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0', padding: '24px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px', borderBottom: '1px solid #f1f5f9', paddingBottom: '16px' }}>
                            <ArchiveRestore color="#f43f5e" size={24} />
                            <h3 style={{ fontSize: '18px', fontWeight: '800', color: '#0f172a', margin: 0 }}>Ocultas / Rascunhos ({vagasInativas.length})</h3>
                        </div>

                        {vagasInativas.length === 0 ? (
                            <p style={{ color: '#64748b', fontSize: '14px', textAlign: 'center' }}>Nenhuma vaga oculta.</p>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                {vagasInativas.map(vaga => (
                                    <div key={vaga.ID} style={{ padding: '16px', borderRadius: '8px', border: '1px solid #e2e8f0', backgroundColor: '#fff1f2', opacity: 0.85 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                            <strong style={{ color: '#0f172a', fontSize: '15px' }}>{vaga.CARGO}</strong>
                                            {/* Substitua a div de botões das Vagas Inativas por esta: */}
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                <button onClick={() => iniciarEdicao(vaga)} style={{ background: 'transparent', border: 'none', color: '#f59e0b', cursor: 'pointer', display: 'flex', gap: '4px', alignItems: 'center', fontSize: '12px', fontWeight: '700' }}><Edit size={16} /> Editar</button>
                                                <button onClick={() => handleAtivar(vaga.ID)} style={{ background: 'transparent', border: 'none', color: '#10b981', cursor: 'pointer', display: 'flex', gap: '4px', alignItems: 'center', fontSize: '12px', fontWeight: '700' }}><Eye size={16} /> Publicar</button>
                                                <button onClick={() => handleExcluir(vaga.ID)} style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', display: 'flex', gap: '4px', alignItems: 'center', fontSize: '12px', fontWeight: '700' }}><Trash2 size={16} /> Excluir</button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                </div>
            </div>
        </div>
    );
};

export default ProcessoSeletivoGestaoVagas;